import { forwardRef, useMemo } from "react";

import { compactNumber } from "@/utils/number";

import {
  CartesianGrid,
  Line,
  LineChart as LineChartComponent,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import ChartLegend from "./ChartLegend";
import { ChartTooltipContent } from "./GroupedChartTooltip";
import {
  buildSeriesFromData,
  ChartSeries,
  domainTickCalculate,
  resolveCategoryKey,
  shouldRotateAxisLabels
} from "./utils";

// Horizontal space reserved by the Y-axis, chart margins and X-axis padding —
// subtracted from the chart width to get the usable plot band width.
const LINE_CHART_AXIS_RESERVED = 115;

interface LineChartProps {
  data: Record<string, string | number>[];
  /** Key used as X-axis category label. Auto-detected if omitted. */
  categoryKey?: string;
  /** One entry per line. Auto-built from data keys if omitted. */
  series?: ChartSeries[];
  height?: number | string;
  title?: string;
  /** Aggregation appended to the legend label when the chart is single-series. */
  aggregation?: string;
}

const truncateLabel = (value: string, maxLength = 12) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;

interface LineChartItemProps {
  width?: number;
  height?: number;
  data: Record<string, string | number>[];
  categoryKey: string;
  series: ChartSeries[];
  axisDomainMax: number;
  axisTicks: number[];
  isGrouped: boolean;
  totalValue: number;
}

const LineChartItem = ({
  width,
  height,
  data,
  categoryKey,
  series,
  axisDomainMax,
  axisTicks,
  isGrouped,
  totalValue
}: LineChartItemProps) => {
  // `width` (injected by ResponsiveContainer) minus the axis footprint gives the
  // plot band. Default to diagonal until it's known to avoid a straight→diagonal flash.
  const isDiagonalLabels =
    !width ||
    shouldRotateAxisLabels(
      data.map((item) => String(item[categoryKey] ?? "")),
      width - LINE_CHART_AXIS_RESERVED
    );

  return (
    <LineChartComponent
      width={width}
      height={height}
      className="overflow-visible"
      data={data}
      margin={{ top: 20, right: 20, left: 10, bottom: 30 }}
    >
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
      <XAxis
        dataKey={categoryKey}
        axisLine={false}
        tickLine={false}
        angle={isDiagonalLabels ? -45 : 0}
        textAnchor={isDiagonalLabels ? "end" : "middle"}
        dx={isDiagonalLabels ? -4 : 0}
        tick={{ fill: "#666", fontSize: 12 }}
        tickFormatter={(v) =>
          isDiagonalLabels ? truncateLabel(String(v)) : String(v)
        }
        interval={0}
        height={70}
        padding={{ left: 20, right: 20 }}
      />
      <YAxis
        domain={[0, axisDomainMax]}
        ticks={axisTicks}
        axisLine={false}
        tickLine={false}
        tick={{ fill: "#666", fontSize: 12 }}
        tickFormatter={(v) => compactNumber(v)}
        width={45}
      />
      <Tooltip
        content={
          <ChartTooltipContent
            isGrouped={isGrouped}
            totalValue={totalValue}
            iconType="line"
          />
        }
      />
      {series.map((s) => (
        <Line
          key={s.dataKey}
          type="monotone"
          dataKey={s.dataKey}
          name={s.label ?? s.dataKey}
          stroke={s.color}
          strokeWidth={2}
          dot={{ r: 4, fill: s.color, stroke: s.color }}
          activeDot={{
            r: 6,
            fill: s.color,
            stroke: "#ffffff",
            strokeWidth: 2
          }}
        />
      ))}
    </LineChartComponent>
  );
};

const LineChart = forwardRef<HTMLDivElement, LineChartProps>((props, ref) => {
  const {
    data,
    categoryKey: categoryKeyProp,
    series: seriesProp,
    height = 400,
    title,
    aggregation
  } = props;

  const categoryKey = useMemo(
    () => resolveCategoryKey(data, categoryKeyProp),
    [categoryKeyProp, data]
  );

  const series = useMemo(
    () => seriesProp ?? buildSeriesFromData(data, categoryKey),
    [seriesProp, data, categoryKey]
  );

  const isGrouped = series.length > 1;

  const numericValues = useMemo(
    () =>
      data
        .flatMap((item) => series.map((s) => Number(item[s.dataKey])))
        .filter((v) => !Number.isNaN(v)),
    [data, series]
  );

  const totalValue = numericValues.reduce((sum, v) => sum + v, 0);

  const maxValue = numericValues.length > 0 ? Math.max(...numericValues, 0) : 0;

  const { axisDomainMax, axisTicks } = useMemo(
    () => domainTickCalculate(numericValues, maxValue),
    [numericValues, maxValue]
  );

  return (
    <div
      ref={ref}
      className="w-full flex flex-col overflow-visible"
      aria-label={title}
    >
      <ResponsiveContainer
        width="100%"
        height={height}
        className="overflow-visible"
      >
        <LineChartItem
          data={data}
          categoryKey={categoryKey}
          series={series}
          axisDomainMax={axisDomainMax}
          axisTicks={axisTicks}
          isGrouped={isGrouped}
          totalValue={totalValue}
        />
      </ResponsiveContainer>
      <ChartLegend series={series} iconType="line" aggregation={aggregation} />
    </div>
  );
});

LineChart.displayName = "LineChart";

export default LineChart;
